"use client";

import { useRouter } from "next/navigation";
import { useState, useCallback } from "react";
import { ProjectProps } from "@/types/project/project.type";
import ProjectSlider from "./ProjectSlider";
import React from "react";
import Link from "next/link";
import { FiArrowUpRight } from "react-icons/fi";
import { useDict, useLocale } from "@/components/providers/LocaleProvider";
import { pickProjectLocale } from "@/libs/project.libs";

interface ProjectCardProps {
  index: number;
  data: ProjectProps;
  isActive: boolean;
  images: string[];
  onHover: (index: number) => void;
  onClick: (index: number) => void;
  isMobileOrTablet: boolean;
  totalProjects: number;
}

function ProjectCard({
  index,
  data,
  isActive,
  images,
  onHover,
  onClick,
  isMobileOrTablet,
}: ProjectCardProps) {
  const router = useRouter();
  const t = useDict();
  const { locale } = useLocale();
  const projectCopy = pickProjectLocale(data, locale);
  const [isHovering, setIsHovering] = useState(false);

  const handleEnter = useCallback(() => {
    if (!isMobileOrTablet) {
      setIsHovering(true);
      onHover(index);
    }
  }, [index, isMobileOrTablet, onHover]);

  const handleLeave = useCallback(() => {
    if (!isMobileOrTablet) setIsHovering(false);
  }, [isMobileOrTablet]);

  // NEW: make touch behave like hover/activate
  const handleTouchStart = useCallback(() => {
    setIsHovering(true);
    onHover(index);
  }, [index, onHover]);

  const handleTouchEnd = useCallback(() => {
    setIsHovering(false);
  }, []);

  const handleClick = () => {
    if (isMobileOrTablet) {
      // If you want tap to expand in a parent list, keep onClick(index).
      // If you prefer to navigate on tap, swap to the router.push line.
      onClick(index);
      // router.push(`/projects/${data.endpoint}`);
    } else {
      router.push(`/projects/${data.endpoint}`);
    }
  };

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      className={`relative cursor-pointer rounded-2xl overflow-hidden flex-1 transition-all duration-700 ease-in-out will-change-transform
        ${
          isActive
            ? "lg:flex-[3] shadow-2xl"
            : "lg:flex-[1] opacity-80 hover:opacity-100"
        }
      `}
      style={{ minHeight: isActive ? 450 : 160 }}
    >
      {/* Project Image Slider */}
      {/* KEY FIX: ensure slider is active on mobile/tablet so images render */}
      <div className="absolute inset-0 h-full w-full">
        <ProjectSlider
          images={images}
          isActive={isActive || isMobileOrTablet}
        />
      </div>

      {/* Overlay */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-end transition-all duration-500 ${
          isActive
            ? "bg-gradient-to-t from-black/70 via-black/30 to-transparent"
            : "bg-black/60"
        }`}
      >
        {/* Inactive card title */}
        {!isActive && (
          <p
            className={`h-full lg:-rotate-90 flex items-center justify-center text-white text-sm md:text-base font-medium tracking-wide px-3 text-center transition-opacity duration-700 ${
              isHovering ? "opacity-100" : "opacity-90"
            }`}
          >
            {projectCopy.title}
          </p>
        )}

        {/* Active card content */}
        <div className="relative overflow-hidden">
          <div
            className={[
              "flex flex-col items-center mt-auto",
              // animation + perf
              "transform-gpu will-change-transform transition duration-400 ease-out",
              "motion-reduce:transition-none motion-reduce:transform-none ",
              // start off-screen-ish & invisible
              isActive
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 translate-y-10 pointer-events-none",
            ].join(" ")}
            aria-hidden={!isActive}
            {...(!isActive ? { tabIndex: -1 } : {})}
          >
            <h3 className="text-white font-semibold text-lg md:text-xl text-center px-4 mb-2">
              {projectCopy.title}
            </h3>

            <div className="flex gap-4 items-center justify-center mb-6">
              <Link
                href={`/projects/${data.endpoint}`}
                className="w-[140px] px-5 py-2 text-sm text-white border border-white rounded-full hover:bg-white/10 transition-colors duration-300 cursor-pointer"
                tabIndex={isActive ? 0 : -1}
              >
                {t.home.projects.viewDetails}
              </Link>

              <Link
                href={data.live}
                target="_blank"
                className="w-[140px] justify-center bg-white text-dark-gray rounded-full px-5 py-2 items-center gap-1.5 font-semibold flex transition-opacity duration-300"
                aria-label={t.home.projects.live}
                tabIndex={isActive ? 0 : -1}
              >
                <span className="text-nowrap">{t.home.projects.live}</span>
                <FiArrowUpRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ProjectCard);
